"use client";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import { useLocale, useTranslations } from "next-intl";
import { Button, buttonVariants } from "./ui/button";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";

const languages = {
  it: "🇮🇹",
  en: "🇺🇸",
};
export default function LanguageSwitcher({
  className,
}: {
  className?: string;
}) {
  const locale = useLocale();
  const t = useTranslations("general");
  const pathname = usePathname();

  const [preferences, setPreferences] = useState(false);

  const setCookie = useCallback(async () => {
    const currentPreferences = window.Cookiebot?.consent?.preferences ?? false;
    setPreferences(currentPreferences);
  }, [setPreferences]);

  useEffect(() => {
    setTimeout(() => setCookie(), 3000);
    window.addEventListener("CookiebotOnAccept", setCookie);
    window.addEventListener("CookiebotOnDecline", setCookie);

    return () => {
      window.removeEventListener("CookiebotOnAccept", setCookie);
      window.removeEventListener("CookiebotOnDecline", setCookie);
    };
  }, [setCookie]);

  const onClick = useCallback(() => {
    window?.Cookiebot?.show();
  }, []);
  return (
    <>
      {!preferences && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="link"
              className={cn("text-[16px]", className)}
            >
              {languages[locale as keyof typeof languages]}{" "}
              {t(locale == "it" ? "italian" : "english")}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("preferencesCookieRequired")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("enableCookie")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={onClick}>
                {t("enable")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {preferences && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="link" className={cn("text-[16px]", className)}>
              {languages[locale as keyof typeof languages]}{" "}
              {t(locale == "it" ? "italian" : "english")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className=" border rounded-md bg-card !z-20">
            <div className="grid px-4 py-2 !z-20 ">
              <a
                aria-disabled={locale == "it"}
                href={`/it${pathname.replace("/en", "")}`}
                className={cn(
                  buttonVariants({ variant: "link", size: "default" }),
                  "hover:no-underline text-[18px]",
                  {
                    "text-muted": locale == "en",
                  }
                )}
                title={t("changeLanguage", { language: t("italian") })}
              >
                🇮🇹 - {t("italian")}
              </a>
              <a
                aria-disabled={locale == "en"}
                href={`/en${pathname.replace("/en", "")}`}
                className={cn(
                  buttonVariants({ variant: "link", size: "default" }),
                  "hover:no-underline text-[18px]",
                  {
                    "text-muted": locale == "it",
                  }
                )}
                title={t("changeLanguage", { language: t("english") })}
              >
                🇺🇸 - {t("english")}
              </a>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}
